/**
 * PSP/CRE Tool v2.0 - MTF Deployment Time Constants
 * 
 * Purpose: Model realistic MTF deployment times for casualty calculations.
 * When units are moving (MARCH, RIVER_CROSSING), MTFs are packed and non-operational.
 * This creates "medical gaps" where DOW rate increases dramatically.
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 21, 2024
 * Version: 2.2 - DOCTRINAL-005: aligned to AJMedP-1 (March 15, 2026)
 *   All setup_hours, breakdown_hours, total_transition_hours updated to AJMedP-1
 *   Section 3.4 values. US FM 4-02 source references removed.
 *   Ukraine observational data retained (real-world validation, not planning factors).
 *   Breakdown time = ~50% of setup time (AJMedP-1 does not specify separately).
 *   Display-only file — calculator uses MTF_SETUP_TIMES_HOURS from
 *   casualtyCalculatorConstants.js.
 *
 * v2.1 CHANGES (January 21, 2026):
 * - BUG-045 FIX: Added normalizePostureValue() helper function
 * - Tab 2 saves posture as object {value: 'X', label: 'Y'} but lookup expects string 'X'
 * - getMTFStatusForPosture() now normalizes posture before lookup
 * - calculateMedicalGap() now normalizes both postures before lookup
 * - STANDARDIZED: All PSP/CRE modules now use DELIBERATE_DEFENSE as default
 * - (Previously this file used RESERVE, causing inconsistent calculations)
 * 
 * Data Sources:
 * - NATO AJMedP-1 Section 3.4 (Deployment and Displacement of Medical Units)
 * - ATP 4-02 series (secondary reference, US-aligned values superseded by AJMedP-1)
 * - Ukraine War Medical Data (2022-2024) — observational, retained for validation
 * 
 * Critical Concepts:
 * - MTFs cannot "teleport" - setup takes time
 * - During movement, medical capability is severely degraded
 * - Prolonged Field Care (PFC) fills the gap when MTFs unavailable
 * - Modern peer warfare makes these gaps longer and more common
 * 
 * Usage:
 * import { 
 *   MTF_DEPLOYMENT_TIMES,
 *   POSTURE_MTF_STATUS,
 *   getMTFStatusForPosture,
 *   calculateMedicalGap
 * } from '../constants/mtfDeploymentTime';
 */

import { ICONS, FACILITY_ICONS, STATUS_ICONS, TACTICAL_ICONS } from '../utils/icons';

// ============================================================================
// MTF DEPLOYMENT TIMES BY ROLE
// ============================================================================

/**
 * Time required for each MTF type to setup, breakdown, and transition
 * 
 * TEACHING POINT:
 * Higher echelon = more capability but SLOWER deployment.
 * Trade-off between surgical capability and responsiveness.
 */
export const MTF_DEPLOYMENT_TIMES = {
  ROLE1: {
    role: 'ROLE1',
    name: "Battalion Aid Station (Role 1)",
    shortName: "BAS",
    icon: FACILITY_ICONS.ROLE1,
    echelon: "BATTALION",
    
    // Time in hours — AJMedP-1 / ENH-006: Role 1 setup ~1h
    setup_hours: 1.0,               // 1 hour to operational (AJMedP-1)
    breakdown_hours: 0.5,           // 30 minutes to pack
    total_transition_hours: 1.5,    // Pack + setup (movement time excluded — scenario-dependent)
    
    // Capability during transition (0.0 - 1.0)
    capability_while_packed: 0.10,      // 10% - only combat medic care
    capability_while_deploying: 0.40,   // 40% - partial capability
    
    // Visual indicators
    status_icons: {
      packed: STATUS_ICONS.RED,
      deploying: STATUS_ICONS.YELLOW,
      operational: STATUS_ICONS.GREEN
    },
    
    // Requirements for setup
    setup_requirements: [
      "Secure area (500m from enemy)",
      "Vehicle access for ambulances",
      "Cover/concealment from observation",
      "Water source within 1km (preferred)"
    ],
    
    // Personnel during transition
    personnel_during_move: {
      combat_medics_with_unit: true,   // Medics stay with troops
      aid_station_packed: true,         // Equipment in vehicles
      limited_resupply: true            // Cannot resupply while moving
    },
    
    // Ukraine data
    ukraine_data: {
      typical_setup_time_minutes: 30,
      rapid_setup_possible: true,
      mobile_role1_common: true,
      notes: 'Ukrainian Role 1 often in vehicles for rapid displacement'
    },
    
    source: "AJMedP-1 Section 3.4; Ukraine observational data"
  },
  
  ROLE2_BASIC: {
    role: 'ROLE2_BASIC',
    name: "Brigade Medical Company (Role 2 Basic)",
    shortName: "BMC",
    icon: FACILITY_ICONS.ROLE2,
    echelon: "BRIGADE",
    
    setup_hours: 12,              // AJMedP-1 Section 3.4 — leap-frog doctrine
    breakdown_hours: 6,           // AJMedP-1 estimate (~50% of setup time)
    total_transition_hours: 18,   // Pack + setup (movement time excluded — scenario-dependent)
    
    capability_while_packed: 0.05,      // 5% - emergency only
    capability_while_deploying: 0.30,   // 30% - limited resuscitation
    
    status_icons: {
      packed: STATUS_ICONS.RED,
      deploying: STATUS_ICONS.YELLOW,
      operational: STATUS_ICONS.GREEN
    },
    
    setup_requirements: [
      "Secure area (2km from forward positions)",
      "Hard surface for vehicles",
      "Power generation capability",
      "Helicopter landing zone within 2km"
    ],
    
    surgical_capability_during_move: false,
    resuscitation_capability_during_move: "LIMITED",
    
    ukraine_data: {
      typical_setup_time_minutes: 60,   // Observational — faster than AJMedP-1 due to improvisation
      frequent_displacement: true,
      hardened_structures_preferred: true,
      notes: 'Ukrainian Role 2 often used schools, hospitals for protection; setup faster than doctrine due to necessity'
    },
    
    source: "AJMedP-1 Section 3.4; Ukraine observational data"
  },
  
  ROLE2_ENHANCED: {
    role: 'ROLE2_ENHANCED',
    name: "Forward Surgical Team (Role 2 Enhanced)",
    shortName: "FST-E",
    icon: FACILITY_ICONS.ROLE2,
    echelon: "DIVISION",
    
    setup_hours: 24,              // AJMedP-1 Section 3.4 — division-level facility
    breakdown_hours: 12,          // AJMedP-1 estimate (~50% of setup time)
    total_transition_hours: 36,   // Pack + setup (movement time excluded — scenario-dependent)
    
    capability_while_packed: 0.0,       // 0% - no surgical capability
    capability_while_deploying: 0.20,   // 20% - limited until OR ready
    
    status_icons: {
      packed: STATUS_ICONS.RED,
      deploying: STATUS_ICONS.YELLOW,
      operational: STATUS_ICONS.GREEN
    },
    
    setup_requirements: [
      "Building or hardstand structure",
      "Reliable power (generator backup)",
      "Water supply for surgical suite",
      "Blood storage capability",
      "Security perimeter established"
    ],
    
    // Surgical-specific timing
    surgical_tables_operational_time: 5.0,  // OR takes extra time
    sterilization_operational_time: 3.0,    // Autoclave setup
    
    ukraine_data: {
      typical_setup_time_hours: 4,   // Observational — faster than AJMedP-1 doctrine due to high-tempo operations
      underground_facilities_critical: true,
      split_operations_common: true,
      notes: 'Ukrainian FSTs often split into smaller mobile elements; setup faster than doctrine under fire'
    },
    
    source: "AJMedP-1 Section 3.4; Ukraine observational data"
  },
  
  ROLE3: {
    role: 'ROLE3',
    name: "Combat Support Hospital (Role 3)",
    shortName: "CSH",
    icon: FACILITY_ICONS.ROLE3,
    echelon: "CORPS",
    
    setup_hours: 48,              // AJMedP-1 Section 3.4 — corps-level hospital
    breakdown_hours: 24,          // AJMedP-1 estimate (~50% of setup time)
    total_transition_hours: 72,   // Pack + setup (movement time excluded — scenario-dependent)
    
    capability_while_packed: 0.0,       // 0% - completely non-operational
    capability_while_deploying: 0.10,   // 10% - emergency triage only
    
    status_icons: {
      packed: STATUS_ICONS.RED,
      deploying: STATUS_ICONS.YELLOW,
      operational: STATUS_ICONS.GREEN
    },
    
    setup_requirements: [
      "Large footprint (2+ hectares)",
      "Hardened or semi-hardened structures",
      "Reliable utilities or significant generator capacity",
      "Helicopter landing zone on-site",
      "Ground evacuation routes secured",
      "Force protection perimeter"
    ],
    
    // Phased operational status — AJMedP-1 Section 3.4 48h setup
    phased_capability: {
      hour_0_to_12:  0.0,   // Setup in progress
      hour_12_to_24: 0.25,  // Emergency triage only
      hour_24_to_36: 0.60,  // Limited surgical capability
      hour_36_to_48: 0.85,  // Approaching full capability
      hour_48_plus:  1.0    // Full operational
    },
    
    ukraine_data: {
      typical_setup_time_hours: 24,   // Observational — faster than AJMedP-1 due to civilian hospital integration
      permanent_facilities_preferred: true,
      civilian_hospital_integration: true,
      notes: 'Ukrainian Role 3 often integrated with existing hospitals — setup faster than doctrine'
    },
    
    source: "AJMedP-1 Section 3.4; Ukraine observational data"
  }
};

// ============================================================================
// POSTURE-BASED MTF OPERATIONAL STATUS
// ============================================================================

/**
 * Medical capability by operational posture
 * 
 * CRITICAL TEACHING POINT:
 * When unit is MOVING, medical is PACKED. 
 * When medical is PACKED, surgical capability = 0.
 * Only combat medic care available during movement.
 */
export const POSTURE_MTF_STATUS = {
  // =========================================================================
  // OFFENSIVE POSTURES - MTF Operational
  // =========================================================================
  
  DELIBERATE_ATTACK: {
    posture: 'DELIBERATE_ATTACK',
    label: 'Deliberate Attack',
    icon: TACTICAL_ICONS.ATTACK,
    mtf_operational: true,
    medical_capability: 1.0,          // 100% - full capability
    mtf_status: "OPERATIONAL",
    status_icon: STATUS_ICONS.GREEN,
    golden_hour_achievable: "HIGH",
    evacuation_routes: "PLANNED",
    pfc_required: false,
    notes: "Planned attack allows medical pre-positioning"
  },
  
  HASTY_ATTACK: {
    posture: 'HASTY_ATTACK',
    label: 'Hasty Attack',
    icon: TACTICAL_ICONS.ATTACK,
    mtf_operational: true,
    medical_capability: 0.90,         // 90% - slight degradation
    mtf_status: "OPERATIONAL",
    status_icon: STATUS_ICONS.GREEN,
    golden_hour_achievable: "MEDIUM",
    evacuation_routes: "EXPEDIENT",
    pfc_required: false,
    notes: "Less preparation time but medical still operational"
  },
  
  EXPLOITATION: {
    posture: 'EXPLOITATION',
    label: 'Exploitation',
    icon: TACTICAL_ICONS.EXPLOITATION,
    mtf_operational: true,
    medical_capability: 0.80,         // 80% - keeping up with advance
    mtf_status: "LEAP_FROGGING",
    status_icon: STATUS_ICONS.YELLOW,
    golden_hour_achievable: "MEDIUM",
    evacuation_routes: "EXTENDING",
    pfc_required: false,
    notes: "Role 2 may lag behind rapid advance"
  },
  
  PURSUIT: {
    posture: 'PURSUIT',
    label: 'Pursuit',
    icon: ICONS.ARROW.THICK_RIGHT,
    mtf_operational: true,
    medical_capability: 0.70,         // 70% - stretched lines
    mtf_status: "LEAP_FROGGING",
    status_icon: STATUS_ICONS.YELLOW,
    golden_hour_achievable: "LOW",
    evacuation_routes: "OVEREXTENDED",
    pfc_required: true,
    notes: "Long evacuation distances, medical struggling to keep pace"
  },
  
  // =========================================================================
  // DEFENSIVE POSTURES - MTF Operational
  // =========================================================================
  
  DELIBERATE_DEFENSE: {
    posture: 'DELIBERATE_DEFENSE',
    label: 'Deliberate Defense',
    icon: TACTICAL_ICONS.DEFEND,
    mtf_operational: true,
    medical_capability: 1.0,          // 100% - optimal conditions
    mtf_status: "HARDENED",
    status_icon: STATUS_ICONS.GREEN,
    golden_hour_achievable: "HIGH",
    evacuation_routes: "ESTABLISHED",
    pfc_required: false,
    notes: "Best medical conditions - prepared positions, known routes"
  },
  
  HASTY_DEFENSE: {
    posture: 'HASTY_DEFENSE',
    label: 'Hasty Defense',
    icon: TACTICAL_ICONS.DEFEND,
    mtf_operational: true,
    medical_capability: 0.85,         // 85% - still good capability
    mtf_status: "OPERATIONAL",
    status_icon: STATUS_ICONS.GREEN,
    golden_hour_achievable: "HIGH",
    evacuation_routes: "EXPEDIENT",
    pfc_required: false,
    notes: "Quick setup but medical functional"
  },
  
  DEFENSE: {
    posture: 'DEFENSE',
    label: 'Defense',
    icon: TACTICAL_ICONS.DEFEND,
    mtf_operational: true,
    medical_capability: 0.95,
    mtf_status: "OPERATIONAL",
    status_icon: STATUS_ICONS.GREEN,
    golden_hour_achievable: "HIGH",
    evacuation_routes: "ESTABLISHED",
    pfc_required: false,
    notes: "Standard defensive posture with good medical support"
  },
  
  RESERVE: {
    posture: 'RESERVE',
    label: 'Reserve',
    icon: TACTICAL_ICONS.RESERVE,
    mtf_operational: true,
    medical_capability: 1.0,          // 100% - full capability
    mtf_status: "STANDBY",
    status_icon: STATUS_ICONS.GREEN,
    golden_hour_achievable: "HIGH",
    evacuation_routes: "ESTABLISHED",
    pfc_required: false,
    notes: "Medical at full readiness, can support other units"
  },
  
  // =========================================================================
  // MOVEMENT POSTURES - MTF NON-OPERATIONAL (CRITICAL!)
  // =========================================================================
  
  MARCH: {
    posture: 'MARCH',
    label: 'Road March',
    icon: TACTICAL_ICONS.MARCH,
    mtf_operational: false,           // CRITICAL: Medical PACKED
    medical_capability: 0.0,          // 0% - surgical = ZERO
    mtf_status: "PACKED",
    status_icon: STATUS_ICONS.RED,
    golden_hour_achievable: "NONE",
    evacuation_routes: "UNAVAILABLE",
    pfc_required: true,               // Combat medics provide all care
    
    // Medical gap details
    medical_gap: {
      duration_typical_hours: "2-8",
      dow_multiplier: 5.0,            // 5x DOW rate during gap
      t1_survival_without_surgery: 0.20
    },
    
    notes: "CRITICAL: All surgical capability = 0%. Combat medics only. " +
           "T1 immediate casualties face 5x mortality without surgery.",
    
    teaching_point: "Medical planners MUST account for surgical gap during movement. " +
                    "PFC training essential. Consider helicopter MEDEVAC to static facility.",
    
    ukraine_data: {
      lessons: [
        'Road marches extremely vulnerable to drone/artillery',
        'Medical gaps during Kharkiv offensive caused preventable deaths',
        'Rapid setup of Role 1 critical upon halt',
        'Pre-positioned medical along route helped'
      ]
    }
  },
  
  RETROGRADE: {
    posture: 'RETROGRADE',
    label: 'Retrograde/Withdrawal',
    icon: TACTICAL_ICONS.WITHDRAWAL,
    mtf_operational: false,           // Medical breaking down
    medical_capability: 0.15,         // 15% - emergency only
    mtf_status: "BREAKDOWN",
    status_icon: STATUS_ICONS.RED,
    golden_hour_achievable: "MINIMAL",
    evacuation_routes: "COMPROMISED",
    pfc_required: true,
    
    medical_gap: {
      duration_typical_hours: "4-12",
      dow_multiplier: 4.0,
      t1_survival_without_surgery: 0.25
    },
    
    notes: "Medical facilities breaking down while unit withdraws. " +
           "Casualties may need to be moved to rear facilities.",
    
    ukraine_data: {
      lessons: [
        'Lysychansk withdrawal showed medical challenges',
        'Early evacuation of patients critical',
        'Rear facilities must prepare for surge',
        'Coordination with higher echelon essential'
      ]
    }
  },
  
  // =========================================================================
  // SPECIAL OPERATIONS - EXTENDED MEDICAL GAP
  // =========================================================================
  
  RIVER_CROSSING: {
    posture: 'RIVER_CROSSING',
    label: 'River Crossing',
    icon: TACTICAL_ICONS.RIVER_CROSSING,
    mtf_operational: false,           // Medical on far side = 0
    medical_capability: 0.0,          // 0% surgical until crossing complete
    mtf_status: "WAITING_TO_CROSS",
    status_icon: STATUS_ICONS.RED,
    golden_hour_achievable: "NONE",
    evacuation_routes: "BLOCKED",
    pfc_required: true,
    
    // Extended gap details
    medical_gap: {
      duration_typical_hours: "12-24",
      dow_multiplier: 5.0,
      t1_survival_without_surgery: 0.18,
      
      phases: {
        assault: { hours: "0-6", capability: 0.10, dow_mult: 5.0 },
        consolidation: { hours: "6-12", capability: 0.15, dow_mult: 4.5 },
        medical_crossing: { hours: "12-14", capability: 0.30, dow_mult: 3.0 },
        established: { hours: "14+", capability: 1.0, dow_mult: 1.0 }
      }
    },
    
    notes: "River crossing creates 12-24 hour medical gap. " +
           "Medical assets cross AFTER combat units establish bridgehead. " +
           "Plan for 2-4 preventable deaths per battalion.",
    
    ukraine_data: {
      lessons: [
        'Siverskyi Donets disaster: 400+ casualties in single crossing',
        'Medical evacuation across river extremely difficult',
        'Helicopter MEDEVAC across river high risk',
        'Forward surgical must cross early with command element'
      ]
    }
  },
  
  AIRBORNE_ASSAULT: {
    posture: 'AIRBORNE_ASSAULT',
    label: 'Airborne Assault',
    icon: ICONS.TRANSPORT.AIRPLANE,
    mtf_operational: false,
    medical_capability: 0.05,
    mtf_status: "JUMP_SURGICAL_ONLY",
    status_icon: STATUS_ICONS.RED,
    golden_hour_achievable: "MINIMAL",
    evacuation_routes: "UNAVAILABLE",
    pfc_required: true,
    
    medical_gap: {
      duration_typical_hours: "24-48",
      dow_multiplier: 4.5,
      t1_survival_without_surgery: 0.22
    },
    
    notes: "Airborne operations have prolonged medical isolation. " +
           "Only jump surgical teams (2-3 surgeons) available until linkup.",
    
    ukraine_data: {
      lessons: [
        'Hostomel: Medical evacuation impossible for 72+ hours',
        'VDV casualties could not be evacuated',
        'Jump surgical capacity insufficient for actual casualties',
        'Modern AD prevents medical helicopter support'
      ]
    }
  },
  
  AIR_ASSAULT: {
    posture: 'AIR_ASSAULT',
    label: 'Air Assault',
    icon: ICONS.TRANSPORT.HELICOPTER,
    mtf_operational: false,
    medical_capability: 0.10,
    mtf_status: "MINIMAL",
    status_icon: STATUS_ICONS.RED,
    golden_hour_achievable: "LOW",
    evacuation_routes: "LIMITED",
    pfc_required: true,
    
    medical_gap: {
      duration_typical_hours: "12-24",
      dow_multiplier: 3.5,
      t1_survival_without_surgery: 0.28
    },
    
    notes: "Air assault better than airborne but still limited medical. " +
           "MEDEVAC helicopters can return casualties if LZ secure."
  }
};

// ============================================================================
// MEDICAL CAPABILITY TO DOW MULTIPLIER CONVERSION
// ============================================================================

/**
 * DOW (Died of Wounds) multiplier based on medical capability
 * 
 * Research basis:
 * - Blackbourne et al (2012): Golden hour mortality reduction
 * - Ukraine 2022-2024: Observed DOW rates by evacuation time
 * - NATO AMedP-8(D): Casualty estimation with medical degradation
 */
export const MEDICAL_CAPABILITY_DOW_MULTIPLIER = {
  // Full capability
  1.0: { dow_multiplier: 1.0, description: "Normal DOW rate" },
  0.9: { dow_multiplier: 1.1, description: "Slightly elevated DOW" },
  0.8: { dow_multiplier: 1.3, description: "Elevated DOW" },
  0.7: { dow_multiplier: 1.5, description: "Significantly elevated DOW" },
  
  // Degraded capability
  0.6: { dow_multiplier: 1.8, description: "High DOW - delayed care" },
  0.5: { dow_multiplier: 2.2, description: "Very high DOW" },
  0.4: { dow_multiplier: 2.6, description: "Critical DOW rate" },
  0.3: { dow_multiplier: 3.0, description: "Severe DOW rate" },
  
  // Minimal/No capability
  0.2: { dow_multiplier: 3.5, description: "Extreme DOW - PFC only" },
  0.1: { dow_multiplier: 4.0, description: "Critical - combat medic only" },
  0.05: { dow_multiplier: 4.5, description: "Catastrophic - buddy aid only" },
  0.0: { dow_multiplier: 5.0, description: "Maximum DOW - no medical care" }
};

/**
 * Get DOW multiplier from capability level
 * @param {number} capability - Medical capability 0.0 - 1.0
 * @returns {number} DOW multiplier
 */
export function getDOWMultiplierFromCapability(capability) {
  // Find closest capability level
  const levels = Object.keys(MEDICAL_CAPABILITY_DOW_MULTIPLIER)
    .map(Number)
    .sort((a, b) => b - a);
  
  for (const level of levels) {
    if (capability >= level) {
      return MEDICAL_CAPABILITY_DOW_MULTIPLIER[level].dow_multiplier;
    }
  }
  
  return 5.0; // Maximum if below all thresholds
}

// ============================================================================
// PROLONGED FIELD CARE (PFC)
// ============================================================================

/**
 * PFC - Extended pre-hospital care when evacuation delayed
 * 
 * TEACHING POINT:
 * PFC is not "waiting to evacuate." It is ACTIVE medical care
 * for hours or even days. Combat medics must be trained in PFC
 * to handle medical gaps from movement, river crossing, etc.
 */
export const PROLONGED_FIELD_CARE = {
  description: "Extended pre-hospital care when evacuation delayed/impossible",
  icon: FACILITY_ICONS.ROLE1,

  // CALC-010: moved here from a literal inside survivalModule (value unchanged, 2026-10-09).
  // PFC is needed when the casualty cannot reach Role 2 within the golden hour (see timeline below).
  activation_threshold_minutes: 60,

  // When PFC is required
  triggers: [
    "MTF not operational (MARCH, RIVER_CROSSING)",
    "Evacuation delayed >2 hours",
    "Mass casualty event overwhelming capacity",
    "Contested airspace preventing MEDEVAC",
    "Unit encircled or cut off"
  ],
  
  // Timeline
  timeline: {
    traditional_golden_hour: "0-60 minutes",
    extended_care: "60-120 minutes",
    prolonged_field_care: "2-72 hours",
    definitive_care_delayed: "May be days in worst case"
  },
  
  // Ukraine 2022-2024 reality
  ukraine_data: {
    pfc_required_percentage: 0.35,              // 35% of casualties
    median_pfc_duration_hours: 8,               // Median 8 hours
    pfc_over_24_hours_percentage: 0.12,         // 12% held >24 hours
    survival_rate_with_pfc: 0.68,               // 68% survive with PFC
    survival_rate_without_pfc: 0.31,            // 31% without PFC capability
    survival_improvement: 0.37,                 // 37% absolute improvement
    
    lessons: [
      'PFC training dramatically improved survival',
      'Whole blood from walking blood bank essential',
      'Hypothermia prevention critical',
      'Documentation enabled continuity of care'
    ]
  },
  
  // Required capabilities
  capabilities: {
    hemorrhage_control: {
      icon: ICONS.MEDICAL.BANDAGE,
      tourniquets: "Maintained/adjusted for hours",
      hemostatic_dressings: "Repacked as needed",
      wound_assessment: "Serial checks for rebleeding"
    },
    airway_management: {
      icon: ICONS.MEDICAL.STETHOSCOPE,
      basic: "NPA, chin lift, recovery position",
      advanced: "Cricothyrotomy if trained",
      duration: "May maintain for hours"
    },
    circulation: {
      icon: ICONS.MEDICAL.SYRINGE,
      iv_access: "Maintain IV/IO for hours",
      fluids: "Limited resuscitation (permissive hypotension)",
      blood_products: "Whole blood if available (walking blood bank)"
    },
    medications: {
      icon: ICONS.MEDICAL.PILL,
      pain_management: "Ketamine, fentanyl for extended periods",
      antibiotics: "Early administration prevents infection",
      txa: "Within 3 hours of injury ONLY"
    },
    monitoring: {
      icon: ICONS.MEDICAL.HEARTBEAT,
      vital_signs: "Serial assessments q15-30min",
      deterioration: "Early detection of decompensation",
      documentation: "Track interventions and times"
    },
    environment: {
      icon: ICONS.WEATHER.THERMOMETER,
      hypothermia_prevention: "CRITICAL - maintain warmth",
      shelter: "Protection from elements",
      positioning: "Optimal for injury type"
    }
  },
  
  // Impact on survival
  impact: {
    with_pfc_training: "68% T1 survival even with 8-hour delay",
    without_pfc_training: "31% T1 survival with 8-hour delay",
    improvement: "37% absolute survival improvement",
    conclusion: "PFC training is MANDATORY for modern combat medics"
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * BUG-045 FIX: Normalize posture value to string
 * 
 * Tab 2 saves posture as an object {value: 'DELIBERATE_DEFENSE', label: '...'}
 * but POSTURE_MTF_STATUS lookup expects just the string 'DELIBERATE_DEFENSE'.
 * This helper extracts the string value from any input format.
 * 
 * STANDARDIZED DEFAULT: DELIBERATE_DEFENSE (0.6× casualty multiplier)
 * - Not too optimistic (RESERVE = 0.0×)
 * - Not too pessimistic (HASTY_DEFENSE = 0.85×)
 * - Consistent across all PSP/CRE modules
 * 
 * @param {string|Object} posture - Posture value (may be object or string)
 * @param {string} defaultPosture - Default if posture is invalid (default: 'DELIBERATE_DEFENSE')
 * @returns {string} Normalized posture string
 */
function normalizePostureValue(posture, defaultPosture = 'DELIBERATE_DEFENSE') {
  // Case 1: Already a valid string
  if (typeof posture === 'string' && posture.length > 0) {
    // Verify it's a valid posture in our lookup table
    if (POSTURE_MTF_STATUS[posture]) {
      return posture;
    }
    // Try uppercase
    const upper = posture.toUpperCase();
    if (POSTURE_MTF_STATUS[upper]) {
      return upper;
    }
    // Unknown string - return default (don't warn here, will warn in calling function)
    return defaultPosture;
  }
  
  // Case 2: Object with various possible property names
  if (posture && typeof posture === 'object') {
    // Try .value first (MUI Select pattern, Tab 2 pattern)
    if (posture.value && typeof posture.value === 'string') {
      return normalizePostureValue(posture.value, defaultPosture);
    }
    // Try .id
    if (posture.id && typeof posture.id === 'string') {
      return normalizePostureValue(posture.id, defaultPosture);
    }
    // Try .key
    if (posture.key && typeof posture.key === 'string') {
      return normalizePostureValue(posture.key, defaultPosture);
    }
    // Object but no extractable value - return default
    return defaultPosture;
  }
  
  // Case 3: null, undefined, or other invalid type
  return defaultPosture;
}

/**
 * Get MTF operational status for a given posture
 * BUG-045 FIX: Now normalizes posture before lookup
 * 
 * @param {string|Object} posture - Operational posture (may be object or string)
 * @returns {Object} MTF status with capability and details
 */
export function getMTFStatusForPosture(posture) {
  // BUG-045 FIX: Normalize posture before lookup
  const normalizedPosture = normalizePostureValue(posture, 'DELIBERATE_DEFENSE');
  const status = POSTURE_MTF_STATUS[normalizedPosture];
  
  if (!status) {
    // This should rarely happen now since normalizePostureValue handles unknown values
    console.warn(`Unknown posture: ${posture}, defaulting to DELIBERATE_DEFENSE`);
    return POSTURE_MTF_STATUS.DELIBERATE_DEFENSE;
  }
  
  return {
    ...status,
    dow_multiplier: getDOWMultiplierFromCapability(status.medical_capability),
    pfc_required: status.pfc_required || false
  };
}

/**
 * Calculate medical gap duration for posture transition
 * @param {string} fromPosture - Starting posture
 * @param {string} toPosture - Ending posture
 * @param {string} mtfRole - MTF role
 * @returns {Object} Gap duration and capability
 */
export function calculateMedicalGap(fromPosture, toPosture, mtfRole = 'ROLE2_BASIC') {
  const deploymentTime = MTF_DEPLOYMENT_TIMES[mtfRole];
  const fromStatus = getMTFStatusForPosture(fromPosture);
  const toStatus = getMTFStatusForPosture(toPosture);
  
  if (!deploymentTime) {
    return { gap_hours: 0, capability_during_gap: 1.0, phase: "NONE" };
  }
  
  // If going from operational to non-operational
  if (fromStatus.mtf_operational && !toStatus.mtf_operational) {
    return {
      gap_hours: deploymentTime.breakdown_hours,
      capability_during_gap: deploymentTime.capability_while_packed,
      dow_multiplier: getDOWMultiplierFromCapability(deploymentTime.capability_while_packed),
      phase: "BREAKDOWN",
      icon: STATUS_ICONS.YELLOW_WARNING
    };
  } 
  // If going from non-operational to operational
  else if (!fromStatus.mtf_operational && toStatus.mtf_operational) {
    return {
      gap_hours: deploymentTime.setup_hours,
      capability_during_gap: deploymentTime.capability_while_deploying,
      dow_multiplier: getDOWMultiplierFromCapability(deploymentTime.capability_while_deploying),
      phase: "SETUP",
      icon: STATUS_ICONS.YELLOW
    };
  }
  
  // No significant gap
  return {
    gap_hours: 0,
    capability_during_gap: toStatus.medical_capability,
    dow_multiplier: getDOWMultiplierFromCapability(toStatus.medical_capability),
    phase: "NONE",
    icon: STATUS_ICONS.GREEN
  };
}

/**
 * Get river crossing medical timeline
 * @returns {Object} Detailed timeline for river crossing
 */
export function getRiverCrossingTimeline() {
  const status = POSTURE_MTF_STATUS.RIVER_CROSSING;
  
  return {
    ...status,
    phases: [
      {
        name: "Assault Phase",
        start_h: 0,
        end_h: 6,
        medical_capability: 0.10,
        dow_multiplier: 5.0,
        icon: STATUS_ICONS.RED,
        description: "Combat troops crossing, only combat medic care"
      },
      {
        name: "Consolidation Phase",
        start_h: 6,
        end_h: 12,
        medical_capability: 0.15,
        dow_multiplier: 4.5,
        icon: STATUS_ICONS.RED,
        description: "Bridgehead secured, casualty collection point established"
      },
      {
        name: "Medical Crossing Phase",
        start_h: 12,
        end_h: 14.5,
        medical_capability: 0.30,
        dow_multiplier: 3.0,
        icon: STATUS_ICONS.YELLOW,
        description: "Medical units crossing, setting up on far side"
      },
      {
        name: "Medical Operational Phase",
        start_h: 14.5,
        end_h: null,
        medical_capability: 1.0,
        dow_multiplier: 1.0,
        icon: STATUS_ICONS.GREEN,
        description: "Full medical capability restored"
      }
    ],
    expected_preventable_deaths_per_battalion: "2-4",
    critical_teaching_point: "Plan for 14+ hour medical gap during river crossing"
  };
}

/**
 * Calculate expected DOW for a medical gap period
 * @param {number} wia_during_gap - Number of WIA during gap
 * @param {number} base_dow_rate - Baseline DOW rate
 * @param {number} medical_capability - Capability during gap
 * @returns {Object} Expected DOW analysis
 */
export function calculateGapDOW(wia_during_gap, base_dow_rate, medical_capability) {
  const dow_multiplier = getDOWMultiplierFromCapability(medical_capability);
  
  const baseline_dow = wia_during_gap * base_dow_rate;
  const gap_dow = wia_during_gap * base_dow_rate * dow_multiplier;
  const additional_dow = gap_dow - baseline_dow;
  
  return {
    baseline_dow: Math.round(baseline_dow),
    gap_dow: Math.round(gap_dow),
    additional_preventable_deaths: Math.round(additional_dow),
    dow_multiplier: dow_multiplier,
    medical_capability: medical_capability,
    icon: dow_multiplier > 3 ? STATUS_ICONS.RED : 
          dow_multiplier > 2 ? STATUS_ICONS.YELLOW : 
          STATUS_ICONS.GREEN
  };
}

/**
 * Get deployment time for MTF role
 * @param {string} role - MTF role
 * @returns {Object} Deployment time configuration
 */
export function getDeploymentTime(role) {
  return MTF_DEPLOYMENT_TIMES[role] || MTF_DEPLOYMENT_TIMES.ROLE2_BASIC;
}

/**
 * Get all postures as array for dropdown
 * @returns {Array} Posture options
 */
export function getPostureOptions() {
  return Object.values(POSTURE_MTF_STATUS).map(status => ({
    value: status.posture,
    label: status.label,
    icon: status.icon,
    operational: status.mtf_operational,
    capability: status.medical_capability,
    status_icon: status.status_icon
  }));
}

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const DEPLOYMENT_TIME_EDUCATIONAL = {
  key_principles: [
    'Higher echelon MTF = more capability but SLOWER deployment',
    'MARCH posture = 0% surgical capability - only combat medic care',
    'River crossing creates 12-24 hour medical gap',
    'PFC bridges the gap when MTF unavailable'
  ],
  
  ukraine_lessons: [
    '35% of casualties required PFC (>2 hour delay)',
    'PFC training improved survival from 31% to 68%',
    'Medical gaps during Kharkiv offensive caused preventable deaths',
    'Whole blood from walking blood bank essential for PFC'
  ],
  
  planning_factors: [
    'Role 1: 45 min setup, 30 min breakdown',
    'Role 2 Basic: 90 min setup, 2 hours breakdown',
    'Role 2 Enhanced: 4.5 hours setup, 5 hours breakdown',
    'Role 3: 18 hours setup, 15 hours breakdown'
  ],
  
  common_errors: [
    'Assuming medical always available during movement',
    'Not planning for river crossing medical gap',
    'Insufficient PFC training for combat medics',
    'Not pre-positioning medical along march route'
  ]
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const mtfDeploymentTimeConstants = {
  MTF_DEPLOYMENT_TIMES,
  POSTURE_MTF_STATUS,
  MEDICAL_CAPABILITY_DOW_MULTIPLIER,
  PROLONGED_FIELD_CARE,
  getDOWMultiplierFromCapability,
  getMTFStatusForPosture,
  calculateMedicalGap,
  getRiverCrossingTimeline,
  calculateGapDOW,
  getDeploymentTime,
  getPostureOptions,
  DEPLOYMENT_TIME_EDUCATIONAL
};

export default mtfDeploymentTimeConstants;
